import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { Alert, Box, Button, Chip, Popover, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material'
import { getAxiosErrorMessage } from 'api/axios/helpers'
import type { AxiosError } from 'axios'
import { CoreMyntModal } from 'components/modals/CoreMyntModal'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import {
  useCategoryGroups,
  useDeleteCategoryGroup,
  useGetCategoryGroupTranslations,
  useUpdateCategoryGroup,
  useUpdateCategoryGroupTranslations,
} from './adapters'
import { isValidOrderInput } from './formUtils'
import type { SpendControlRow } from './SpendControlTable'
import { TranslationsTab } from './TranslationsTab'
import { useTranslationsFields } from './useTranslationsFields'

type MainTab = 'details' | 'translations'

type FormValues = {
  name: string
  description: string
  order: string
}

const schema = yup.object({
  name: yup
    .string()
    .trim()
    .required()
    .test('not-dash', 'Name cannot be "-"', (v) => v?.trim() !== '-'),
  description: yup.string().default(''),
  order: yup
    .string()
    .default('')
    .test('valid-order', 'Must be a non-negative integer', (v) => isValidOrderInput(v ?? '')),
})

export const EditCategoryGroupModal = ({
  open,
  onClose,
  row,
  partner,
}: {
  open: boolean
  onClose: () => void
  row: SpendControlRow
  partner: string | undefined
}) => {
  const [mainTab, setMainTab] = useState<MainTab>('details')
  const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [categoryIds, setCategoryIds] = useState<string[]>([...new Set(row.categoryIds ?? [])])

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { isValid, isDirty, errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: row.name,
      description: row.description ?? '',
      order: row.order != null ? String(row.order) : '',
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const { data: loadedTranslations } = useGetCategoryGroupTranslations({ categoryGroupId: row.id })
  const {
    translationFields,
    updateTranslation,
    markBlurred,
    buildPatchPayload,
    translationsValidationErrors,
    hasTranslationsValidationErrors,
    isTranslationsChanged,
  } = useTranslationsFields(loadedTranslations, { requireDescription: false })

  const { mutate: mutateDetails, isLoading: detailsLoading, error: detailsError, reset: detailsReset } = useUpdateCategoryGroup()
  const { mutate: mutateTranslations, isLoading: transLoading, error: transError } = useUpdateCategoryGroupTranslations()
  const { mutate: mutateDelete, isLoading: deleteLoading, error: deleteError } = useDeleteCategoryGroup()
  const { data: allRows = [] } = useCategoryGroups({ partner })

  const allCategories = useMemo(
    () => [...new Map(allRows.filter((r) => r.categoryIds === undefined).map((r) => [r.id, r])).values()],
    [allRows],
  )
  const assignedCategories = useMemo(
    () => categoryIds.map((id) => allCategories.find((c) => c.id === id)).filter((c): c is SpendControlRow => c != null),
    [categoryIds, allCategories],
  )
  const availableCategories = useMemo(() => allCategories.filter((c) => !categoryIds.includes(c.id)), [allCategories, categoryIds])

  const categoryIdsChanged = JSON.stringify([...categoryIds].sort()) !== JSON.stringify([...(row.categoryIds ?? [])].sort())
  const isChanged = isDirty || categoryIdsChanged

  const handleClose = () => {
    resetForm()
    setCategoryIds([...new Set(row.categoryIds ?? [])])
    detailsReset()
    onClose()
  }

  const onDetailsSubmit = handleSubmit((data) => {
    const parsedOrder = data.order.trim() === '' ? null : Number(data.order.trim())
    mutateDetails(
      { id: row.id, name: data.name.trim(), description: data.description.trim() || null, categoryIds, order: parsedOrder },
      { onSuccess: handleClose },
    )
  })

  const handleTranslationsSave = () => {
    const patchPayload = buildPatchPayload()
    if (patchPayload.length > 0) {
      mutateTranslations({ id: row.id, translations: patchPayload }, { onSuccess: handleClose })
    }
  }

  const hasCategories = (row.categoryIds?.length ?? 0) > 0

  return (
    <CoreMyntModal
      wrapperProps={{ style: { minWidth: 800 } }}
      title="Edit category group"
      onClose={handleClose}
      open={open}
      actions={() => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Box>
            {mainTab === 'details' && (
              <>
                <Tooltip title={hasCategories ? 'Remove all categories before deleting this group' : ''}>
                  <span>
                    <Button
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      disabled={hasCategories}
                      onClick={(e) => setDeleteAnchorEl(e.currentTarget)}
                    >
                      Delete group
                    </Button>
                  </span>
                </Tooltip>
                <Popover
                  open={Boolean(deleteAnchorEl)}
                  anchorEl={deleteAnchorEl}
                  onClose={() => setDeleteAnchorEl(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      minWidth: 180,
                    }}
                  >
                    <Typography fontWeight="bold">Are you sure?</Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button onClick={() => setDeleteAnchorEl(null)}>Cancel</Button>
                      <Button
                        variant="contained"
                        color="error"
                        disabled={deleteLoading}
                        onClick={() => mutateDelete(row.id, { onSuccess: handleClose })}
                      >
                        {deleteLoading ? 'Deleting...' : 'Yes'}
                      </Button>
                    </Box>
                  </Box>
                </Popover>
              </>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            {mainTab === 'details' && (
              <Button variant="contained" disabled={!isValid || !isChanged || detailsLoading} onClick={onDetailsSubmit}>
                {detailsLoading ? 'Saving...' : 'Save'}
              </Button>
            )}
            {mainTab === 'translations' && (
              <Button
                variant="contained"
                disabled={!isTranslationsChanged || hasTranslationsValidationErrors || transLoading}
                onClick={handleTranslationsSave}
              >
                {transLoading ? 'Saving...' : 'Save translations'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {detailsError instanceof Error && mainTab === 'details' && (
          <Alert severity="error">
            {(detailsError as AxiosError).response ? getAxiosErrorMessage(detailsError as AxiosError) : detailsError.message}
          </Alert>
        )}
        {deleteError instanceof Error && mainTab === 'details' && (
          <Alert severity="error">
            {(deleteError as AxiosError).response ? getAxiosErrorMessage(deleteError as AxiosError) : deleteError.message}
          </Alert>
        )}
        {transError instanceof Error && mainTab === 'translations' && (
          <Alert severity="error">
            {(transError as AxiosError).response ? getAxiosErrorMessage(transError as AxiosError) : transError.message}
          </Alert>
        )}

        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}>
          <Tab label="Details" value="details" />
          <Tab label="Translations" value="translations" />
        </Tabs>

        {mainTab === 'details' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <TextField label="Name" {...register('name')} required />
            <TextField label="Description" {...register('description')} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Categories
              </Typography>
              {assignedCategories.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  {assignedCategories.map((cat) => (
                    <Chip
                      key={cat.id}
                      label={cat.name}
                      size="small"
                      onDelete={() => setCategoryIds((prev) => prev.filter((id) => id !== cat.id))}
                    />
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.disabled',
                    fontSize: 12,
                  }}
                >
                  No categories assigned
                </Typography>
              )}
              {availableCategories.length > 0 && (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mt: 0.5,
                    }}
                  >
                    Add categories
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                    }}
                  >
                    {availableCategories.map((cat) => (
                      <Chip
                        key={cat.id}
                        label={cat.name}
                        size="small"
                        variant="outlined"
                        onClick={() => setCategoryIds((prev) => [...prev, cat.id])}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
            <TextField label="Order" type="number" {...register('order')} error={!!errors.order} />
          </Box>
        )}

        {mainTab === 'translations' && (
          <TranslationsTab
            currentName={row.name}
            currentDescription={row.description}
            translationFields={translationFields}
            translationsValidationErrors={translationsValidationErrors}
            onUpdate={updateTranslation}
            onBlur={markBlurred}
          />
        )}
      </Box>
    </CoreMyntModal>
  )
}
